import Moment from 'moment';
import { isEmpty, isNil, prop, propOr } from 'ramda';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ErrorBoundary } from 'react-error-boundary';
import { hot } from 'react-hot-loader/root';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { AuthRedirect, AuthRedirectProvider } from '@fasstech/react-auth-redirect';
import Account from './pages/account';
import Administration from './pages/administration/Routes';
import Frames from './pages/frames';
import NotFound from './pages/errors/NotFound';
import OfferRoutes from './pages/offer/Routes';
import Team from './pages/team';
import JoinTeam from './pages/team/components/JoinTeam';
import User from './User';
import { UserContext } from './withUser';
import ProtectedRoute from './ProtectedRoute';
import { ROLES } from './lib/rights';
import Models from './pages/models';
import RepriseDocument from './pages/docs/RepriseDocument';
import Offers from './pages/offer/Offers';
import { renderWithBreadcrumb, renderWithoutBreadcrumb } from './pages/helpers/breadCrumb';
import EmptyTeams from './pages/errors/EmptyTeams';
import { useClosePopupsOnScroll } from './hooks';

Moment.locale('fr');

const EmptyTeamsPage = renderWithoutBreadcrumb(EmptyTeams);
const OffersPage = renderWithBreadcrumb(Offers);
const TeamPage = renderWithoutBreadcrumb(Team);
const AdministrationPage = renderWithoutBreadcrumb(Administration);

const FallbackComponent = ({ componentStack, error }) => (
  <div>
    <p><strong>Oops! An error occurred!</strong></p>
    <p>Here’s what we know…</p>
    <p><strong>Error:</strong> {error.toString()}</p>
    <p><strong>Stacktrace:</strong> {componentStack}</p>
  </div>
);

const Routes = () => {
  useClosePopupsOnScroll();

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent}>
      <User refreshInterval={60000}>
        {({ user, userTeam, userRefetch, teams }) => {
          const teamIsEmpty = isEmpty(teams) && isNil(prop('teamId', userTeam));
          return (
            <UserContext.Provider
              value={{
                user, userTeam, userRefetch, teams
              }}
            >
              <AuthRedirectProvider>
                <DndProvider backend={HTML5Backend}>
                  <Router>
                    <Switch>
                      <Route path="/account" component={Account}/>
                      <Route path="/join/team" exact component={JoinTeam}/>
                      <Route path="/documents/reprise" component={RepriseDocument} />
                      <AuthRedirect
                        when={isNil(propOr(null, 'email', user))}
                        redirectRender={<Redirect to="/account/signin"/>}
                      >
                        <Switch>
                          <Route exact path="/offre" render={() => <Redirect to="/" />} />
                          <Route path="/" exact component={teamIsEmpty ? EmptyTeamsPage : OffersPage}/>
                          <ProtectedRoute path="/equipe" Component={TeamPage} roles={[ROLES.admin]} />
                          <ProtectedRoute path="/offre" Component={OfferRoutes} roles={[ROLES.admin, ROLES.offerEditor, ROLES.regular, ROLES.qa]} />
                          <ProtectedRoute path="/trames" Component={Frames} roles={[ROLES.admin, ROLES.qa]} />
                          <ProtectedRoute path="/modeles" Component={Models} roles={[ROLES.admin, ROLES.offerEditor, ROLES.regular, ROLES.qa]} />
                          <ProtectedRoute path="/administration" Component={AdministrationPage} roles={[ROLES.superAdmin]} />
                          <Route component={NotFound}/>
                        </Switch>
                      </AuthRedirect>
                      <Route component={NotFound}/>
                    </Switch>
                  </Router>
                </DndProvider>
              </AuthRedirectProvider>
            </UserContext.Provider>
          );
        }}
      </User>
    </ErrorBoundary>
  );
};

export default hot(Routes);
